
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export const CoordinateDebugger = () => {
  const [debugInfo, setDebugInfo] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const checkCurrentState = async () => {
    try {
      setLoading(true);
      console.log('🔍 Checking current profile state (FINAL SYSTEM)...');

      // Gate behind session check
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        throw new Error('No authenticated session found');
      }

      // Get current profile data only if session exists
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', session.user.id)
        .maybeSingle();

      if (profileError) {
        throw profileError;
      }

      console.log('✅ Current profile data (FINAL SYSTEM):', profile);

      setDebugInfo({
        profile,
        timestamp: new Date().toISOString(),
        coordinatesStatus: profile.latitude && profile.longitude ? 'POPULATED' : 'MISSING',
        locationPointStatus: profile.location_point ? 'POPULATED' : 'MISSING',
        systemVersion: 'FINAL SIMPLIFIED SYSTEM',
        expectedForEvans: {
          latitude: 33.5379,
          longitude: -82.1265
        },
        triggerFunction: 'populate_coordinates_from_location()',
        lookupFunction: 'get_coordinates_from_location()'
      });

      const coordStatus = profile.latitude && profile.longitude ? 'populated' : 'missing';
      const pointStatus = profile.location_point ? 'populated' : 'missing';
      
      toast({
        title: "State Check Complete (FINAL SYSTEM)",
        description: `Location: "${profile.location}" | Coordinates: ${coordStatus} | Point: ${pointStatus}`,
      });

    } catch (error: any) {
      console.error('❌ Debug error (FINAL SYSTEM):', error);
      setDebugInfo({
        error: error.message,
        timestamp: new Date().toISOString(),
        systemVersion: 'FINAL SIMPLIFIED SYSTEM'
      });
      
      toast({
        title: "Debug Error (FINAL SYSTEM)",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const testFinalSystem = async () => {
    try {
      setLoading(true);
      console.log('🚀 Testing FINAL SIMPLIFIED SYSTEM...');

      // Gate behind session check
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        throw new Error('No authenticated session found');
      }

      // Test the FINAL system by updating location
      console.log('📍 Updating profile with Evans, GA location (FINAL SYSTEM)...');
      
      const { data, error } = await supabase
        .from('profiles')
        .update({ 
          location: 'Evans, GA',
          updated_at: new Date().toISOString()
        })
        .eq('user_id', session.user.id)
        .select('id, location, latitude, longitude, location_point, updated_at')
        .maybeSingle();

      if (error) {
        console.error('❌ FINAL SYSTEM update error:', error);
        throw error;
      }

      console.log('✅ FINAL SYSTEM update result:', {
        id: data.id,
        location: data.location,
        latitude: data.latitude,
        longitude: data.longitude,
        location_point: data.location_point ? 'POPULATED' : 'NULL',
        updated_at: data.updated_at
      });

      // Check if BOTH coordinates AND location_point were populated by FINAL system
      const hasCoords = data.latitude && data.longitude;
      const hasPoint = data.location_point !== null;

      if (hasCoords && hasPoint) {
        console.log('🎉 FINAL SYSTEM SUCCESS: Both coordinates AND location_point populated!');
        toast({
          title: "🎉 FINAL SYSTEM Success!",
          description: `Coordinates: ${data.latitude}, ${data.longitude} | Location Point: ✅ Created`,
        });
      } else if (hasCoords && !hasPoint) {
        console.log('⚠️ FINAL SYSTEM: Coordinates populated but location_point still missing');
        toast({
          title: "⚠️ FINAL SYSTEM Partial Success",
          description: "Coordinates populated but location_point still missing",
          variant: "destructive",
        });
      } else {
        console.log('❌ FINAL SYSTEM: No automatic population occurred');
        toast({
          title: "❌ FINAL SYSTEM Failed",
          description: "No automatic population occurred",
          variant: "destructive",
        });
      }

      // Refresh debug info
      await checkCurrentState();

    } catch (error: any) {
      console.error('❌ FINAL SYSTEM test error:', error);
      
      toast({
        title: "FINAL SYSTEM Test Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-2xl">
      <CardHeader>
        <CardTitle className="text-green-600">✨ FINAL Simplified System Debugger</CardTitle>
        <p className="text-sm text-gray-600">
          Database cleaned up. Testing FINAL simplified trigger system.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2 flex-wrap">
          <Button 
            onClick={checkCurrentState}
            disabled={loading}
            variant="outline"
          >
            Check Current State
          </Button>
          
          <Button 
            onClick={testFinalSystem}
            disabled={loading}
            className="bg-green-600 hover:bg-green-700"
          >
            ✨ Test FINAL System
          </Button>
        </div>

        {debugInfo && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-semibold mb-2">Debug Information (FINAL SYSTEM):</h3>
            <pre className="text-sm overflow-auto max-h-96">
              {JSON.stringify(debugInfo, null, 2)}
            </pre>
          </div>
        )}

        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <h4 className="font-semibold text-green-800 mb-2">✨ FINAL Simplified System Status</h4>
          <ul className="text-sm text-green-700 space-y-1">
            <li>• ✅ All old conflicting triggers completely removed</li>
            <li>• ✅ FINAL trigger created: populate_coordinates_from_location_trigger</li>
            <li>• ✅ FINAL lookup function: get_coordinates_from_location()</li>
            <li>• ✅ System tested during migration with existing data</li>
            <li>• ✅ Clean, reliable single-trigger architecture</li>
            <li>• 🎯 Expected: Evans, GA → (33.5379, -82.1265) + PostGIS point</li>
          </ul>
        </div>

        <div className="text-sm text-gray-600">
          <p><strong>FINAL System Test Results Expected:</strong></p>
          <ul className="list-disc list-inside space-y-1 mt-2">
            <li><strong>Check Current State:</strong> Shows current profile data with system info</li>
            <li><strong>Test FINAL System:</strong> Should populate BOTH coordinates AND location_point automatically</li>
            <li><strong>Success Criteria:</strong> Both lat/lng and PostGIS point created in single operation</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};
